import React, { useEffect } from "react";
import { Grid, Paper, Stack, Typography, Box } from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

import { defaultPaginate, PERMISSIONS } from "../../../helpers/constants";
import {
    useGenerateNewDatabaseBackupMutation,
    useGetDatabaseBackupListQuery,
    useCleanupOldBackupsMutation,
} from "../../../store/common/commonApis";
import BackupTableComponent from "../../../components/admin/database-backup/BackupTable";
import Button from "../../../components/Common/Button";
import HasPermission from "../../../components/Common/HasPermission";
import { useDispatch } from "react-redux";
import { setSnackBar } from "../../../store/common/commonSlice";

const ActivityManagerContainer = (props) => {
    const dispatch = useDispatch();
    const { pagination } = props;
    const { isLoading, data, isFetching } = useGetDatabaseBackupListQuery({ ...pagination });
    const [generateBackup, { isLoading: isLoadingBackup }] = useGenerateNewDatabaseBackupMutation();
    const [cleanupBackups, { isLoading: isLoadingCleanup }] = useCleanupOldBackupsMutation();

    useEffect(() => {
        props.handlePaginationState(defaultPaginate);
    }, []);

    const handlePagination = (setting) => {
        props.handlePaginationState(setting);
    };

    const downloadBlob = (blob, filename) => {
        if (!blob) {
            throw new Error("Backup file response is empty");
        }

        const fileUrl = window.URL.createObjectURL(blob);
        const anchor = document.createElement("a");
        anchor.href = fileUrl;
        anchor.download = filename || `backup-${Date.now()}.zip`;
        document.body.appendChild(anchor);
        anchor.click();
        anchor.remove();
        window.URL.revokeObjectURL(fileUrl);
    };

    const handleGenerateBackup = async () => {
        try {
            const response = await generateBackup().unwrap();
            downloadBlob(response?.blob, response?.filename);
            dispatch(
                setSnackBar({
                    open: true,
                    message: "Manual backup downloaded. Server copy will auto-delete after 7 days.",
                    severity: "success",
                }),
            );
        } catch (err) {
            dispatch(
                setSnackBar({
                    open: true,
                    message: err?.data?.message || err.message || "Backup generation failed",
                    severity: "error",
                }),
            );
        }
    };

    const handleCleanup = async () => {
        if (!window.confirm("This will delete all backups older than 7 days from MongoDB and server storage. Continue?")) {
            return;
        }
        try {
            await cleanupBackups().unwrap();
            dispatch(setSnackBar({ open: true, message: "Cleanup completed", severity: "success" }));
        } catch (err) {
            dispatch(
                setSnackBar({
                    open: true,
                    message: err?.data?.message || err.message || "Cleanup failed",
                    severity: "error",
                }),
            );
        }
    };

    return (
        <Stack spacing={1}>
            <Paper sx={{ marginBottom: "24px", padding: 1.5 }}>
                <Box sx={{ mb: 1, display: "flex", alignItems: "center", gap: 0.5 }}>
                    <InfoOutlinedIcon fontSize="small" color="action" />
                    <Typography variant="body2" color="text.secondary">
                        Auto backup runs daily at 1:00 AM IST. Only the last 7 days of backups are retained.
                    </Typography>
                </Box>
                <Grid container justifyContent="space-between">
                    <Grid item xs={6} sx={{ alignSelf: "center" }}>
                        <Typography variant="h6">List of Activity</Typography>
                    </Grid>
                    <Grid
                        item
                        xs={6}
                        sx={{ alignSelf: "center" }}
                        display={"flex"}
                        flexDirection={"row"}
                        justifyContent={"flex-end"}
                        gap={1}
                    >
                        <HasPermission permission={PERMISSIONS.SETTINGS.READ} fallback={null}>
                            <Button
                                variant="outlined"
                                fullWidth={false}
                                onClick={handleCleanup}
                                loading={isLoadingCleanup}
                            >
                                Cleanup Old Backups
                            </Button>
                            <Button
                                variant="contained"
                                fullWidth={false}
                                onClick={handleGenerateBackup}
                                loading={isLoadingBackup}
                            >
                                Generate Backup &amp; Download
                            </Button>
                        </HasPermission>
                    </Grid>
                </Grid>
            </Paper>

            <Grid container>
                <Grid item xs={12}>
                    <Paper sx={{ p: 2, color: "#071B2A", fontWeight: "400" }} elevation={0}>
                        <BackupTableComponent
                            loading={isLoading}
                            fetching={isFetching}
                            count={data?.count}
                            data={data?.result || []}
                            pagination={pagination}
                            handlePagination={(val) => handlePagination(val)}
                        />
                    </Paper>
                </Grid>
            </Grid>
        </Stack>
    );
};

export default ActivityManagerContainer;
